import { RefreshCw, Wallet } from "lucide-react";
import { useState } from "react";
import { SiFacebook, SiInstagram, SiMeta } from "react-icons/si";
import { toast } from "sonner";

import { anunciosMetaApi, dataHoraBR, mensagemDeErro, reais } from "./api";

/**
 * Topo da tela conectada: o que está ligado (conta de anúncio, Página,
 * Instagram), o dinheiro da conta e quando foi a última leitura.
 */
function Item({ icone, rotulo, alerta, titulo, children }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0" title={titulo}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${alerta ? "bg-amber-500/15 text-amber-600" : "bg-muted text-muted-foreground"}`}>
        {icone}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{rotulo}</p>
        <p className={`text-sm font-semibold truncate ${alerta ? "text-amber-600" : ""}`}>{children}</p>
      </div>
    </div>
  );
}

function instagramDaConexao(conexao, legivel) {
  if (conexao.instagram_username) return { texto: `@${conexao.instagram_username}` };
  if (!legivel) return { texto: "Ainda não lido", titulo: "O Núcleo ainda não consegue ler o Instagram desta Página." };
  return { texto: "Não ligado à Página", alerta: true, titulo: "No Instagram: Editar perfil → Página → conectar a esta Página." };
}

function dinheiroDaConta(conexao, saldoMinimo) {
  if (conexao.pre_paga) {
    if (conexao.saldo_centavos == null) return { rotulo: "Saldo (pré-pago)", texto: "—" };
    return { rotulo: "Saldo (pré-pago)", texto: reais(conexao.saldo_centavos), alerta: conexao.saldo_centavos < saldoMinimo };
  }
  return conexao.tem_pagamento
    ? { rotulo: "Pagamento", texto: "Forma de pagamento ok" }
    : { rotulo: "Pagamento", texto: "Sem forma de pagamento", alerta: true };
}

export default function FaixaStatus({ conexao, instagramLegivel, saldoMinimo, podeGerir, onMudou }) {
  const [ocupado, setOcupado] = useState(false);
  const instagram = instagramDaConexao(conexao, instagramLegivel);
  const dinheiro = dinheiroDaConta(conexao, saldoMinimo);

  const atualizar = async () => {
    setOcupado(true);
    try {
      const r = await anunciosMetaApi.sincronizar();
      toast.success(`Lido: ${r.campanhas} campanhas, ${r.insights} linhas de gasto.`);
      onMudou();
    } catch (e) { toast.error(mensagemDeErro(e, "Não foi possível ler a Meta agora.")); }
    finally { setOcupado(false); }
  };

  return (
    <section className="rounded-xl border bg-card p-4 sm:p-5 space-y-4" data-e2e="meta-conectada">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <h2 className="font-bold">Conectada à Meta</h2>
          <span className="text-xs text-muted-foreground">última leitura {dataHoraBR(conexao.ultimo_sync_em)}</span>
        </div>
        {podeGerir && (
          <button onClick={atualizar} disabled={ocupado} data-e2e="meta-atualizar"
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:border-primary disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${ocupado ? "animate-spin" : ""}`} /> Atualizar agora
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Item icone={<SiMeta className="w-4 h-4" />} rotulo="Conta de anúncio" titulo={conexao.ad_account_id}>{conexao.ad_account_nome || "—"}</Item>
        <Item icone={<SiFacebook className="w-4 h-4" />} rotulo="Página do Facebook">{conexao.page_nome || "—"}</Item>
        <Item icone={<SiInstagram className="w-4 h-4" />} rotulo="Instagram" alerta={instagram.alerta} titulo={instagram.titulo}>
          <span data-e2e="meta-instagram">{instagram.texto}</span>
        </Item>
        <Item icone={<Wallet className="w-4 h-4" />} rotulo={dinheiro.rotulo} alerta={dinheiro.alerta}>
          <span data-e2e="meta-saldo">{dinheiro.texto}</span>
        </Item>
      </div>
    </section>
  );
}
